/**
 * BrandingProvider — aplica config visual del CO activo:
 *   - CSS custom properties --primary/--accent (+ sus *-foreground) del color hex
 *   - <link rel="icon"> favicon dinámico
 *   - document.title con nombre_comercial
 *
 * Se monta en App.tsx root. Reacciona a cambios de activeCoId y branding.
 *
 * FIX contraste (saneo A-front): el theme consume las variables como
 * `hsl(var(--primary))` → DEBEN estar en formato HSL sin función ("H S% L%"),
 * NO en hex. Inyectar `#rrggbb` crudo producía `hsl(#0f172a)` (inválido) → el
 * navegador descartaba la regla → bg-primary TRANSPARENTE y el texto
 * primary-foreground quedaba invisible sobre el fondo (la burbuja del usuario y
 * el botón Enviar del chat, entre otros). Aquí se convierte hex→HSL y se calcula
 * el *-foreground (blanco/negro) por luminancia para garantizar contraste.
 */
import { useEffect, type ReactNode } from 'react'
import { useAuthStore } from '@/stores/auth'
import { useBrandingStore } from '@/stores/branding'

/** hex "rrggbb" → triplete HSL "H S% L%" (el formato que el theme mete en hsl()). */
function hexToHslTriplet(hex: string): string | null {
  if (!/^[0-9a-f]{6}$/i.test(hex)) return null
  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0, s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0)
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h /= 6
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/** Foreground legible (HSL triplete) según luminancia percibida del color de fondo. */
function foregroundFor(hex: string): string {
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255 // luma Rec.601
  return lum > 0.6 ? '222 47% 11%' : '0 0% 100%' // fondo claro → texto oscuro; oscuro → blanco
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const activeCoId = useAuthStore((s) => s.activeCoId)
  const activo = useBrandingStore((s) => s.activo)
  const applyForCo = useBrandingStore((s) => s.applyForCo)

  // Cuando cambia activeCoId, re-aplicar branding correspondiente
  useEffect(() => {
    applyForCo(activeCoId)
  }, [activeCoId, applyForCo])

  // Aplicar a CSS custom properties (formato HSL "H S% L%" — ver nota de cabecera).
  // Cada color de marca fija TAMBIÉN su *-foreground por contraste, para que el par
  // bg-X / text-X-foreground sea siempre legible (toggle claro/oscuro incluido).
  useEffect(() => {
    const root = document.documentElement
    const aplicar = (colorVar: string, fgVar: string, valor?: string) => {
      const hex = (valor || '').replace(/^#/, '')
      const hsl = hexToHslTriplet(hex)
      if (hsl) {
        root.style.setProperty(colorVar, hsl)
        root.style.setProperty(fgVar, foregroundFor(hex))
      } else {
        root.style.removeProperty(colorVar)
        root.style.removeProperty(fgVar)
      }
    }
    aplicar('--primary', '--primary-foreground', activo.color_primario)
    aplicar('--accent', '--accent-foreground', activo.color_acento)
  }, [activo.color_primario, activo.color_acento])

  // Aplicar favicon dinámico
  useEffect(() => {
    if (!activo.favicon_url) return
    const existing = document.querySelector("link[rel='icon']") as HTMLLinkElement | null
    if (existing) {
      existing.href = activo.favicon_url
    } else {
      const link = document.createElement('link')
      link.rel = 'icon'
      link.href = activo.favicon_url
      document.head.appendChild(link)
    }
  }, [activo.favicon_url])

  // Aplicar document.title con nombre_comercial
  useEffect(() => {
    if (activo.nombre_comercial) {
      document.title = `${activo.nombre_comercial} · aarpia-lite`
    } else {
      document.title = 'Arquitecto · aarpia-lite'
    }
  }, [activo.nombre_comercial])

  return <>{children}</>
}
